/**
 * A section, with its heading block.
 *
 * Nine sections share one rhythm from the kit — 72px of vertical padding, an
 * uppercase mono eyebrow, an Archivo 700 heading, an optional lead capped at
 * 660px, then 40–44px of air before the content. Defining it once means a new
 * section cannot drift out of step by mistyping a value.
 *
 * `tone` picks the ground. The kit alternates bone and paper so sections
 * separate without a rule between them, and `ink` is the inverted panel.
 */
import type { ReactNode } from 'react';
import styles from './Section.module.css';

interface SectionProps {
  id?: string;
  eyebrow?: string;
  title?: string;
  lead?: string;
  tone?: 'bone' | 'paper' | 'ink';
  /** Renders the heading for screen readers only — for sections that are all content. */
  children: ReactNode;
}

export function Section({ id, eyebrow, title, lead, tone = 'bone', children }: SectionProps) {
  const hasHeader = Boolean(eyebrow || title || lead);

  return (
    <section id={id} className={`${styles.section} ${styles[tone]}`}>
      <div className="container">
        {hasHeader ? (
          <div className={styles.header} data-reveal="">
            {eyebrow ? <p className="overline">{eyebrow}</p> : null}
            {title ? <h2 className={styles.title}>{title}</h2> : null}
            {lead ? <p className="lead">{lead}</p> : null}
          </div>
        ) : null}
        {children}
      </div>
    </section>
  );
}
